import type { DOMOutputSpec, Node as ProsemirrorNode, NodeSpec } from 'prosemirror-model';
import { NodeSelection } from 'prosemirror-state';
import type { EditorView, NodeView } from 'prosemirror-view';

import type { RtSchemaType } from '../../../editor/types';
import { HtmlClassExtension } from '../../../extensions/html-class';
import { NodeName } from '../../../schema/nodes';

export class ColumnsNode implements NodeSpec {
  public static readonly nodeName = NodeName.Columns;
  public static is(node: ProsemirrorNode) {
    return node.type.name === ColumnsNode.nodeName;
  }

  private readonly htmlClassExtension?: HtmlClassExtension;

  public attrs = {
    ...(this.config?.withHtmlClass && HtmlClassExtension.attrs),
  };

  public group = 'columns';
  public content = 'column{2,4}';
  public selectable = true;

  public parseDOM = [
    {
      tag: 'div.columns',
      getAttrs: (node: HTMLElement) => ({
        ...(this.htmlClassExtension && this.htmlClassExtension.parseDOMGetAttrs(node)),
      }),
    },
  ];

  constructor(private config?: { withHtmlClass?: boolean }) {
    if (config?.withHtmlClass) this.htmlClassExtension = new HtmlClassExtension();
  }

  public toDOM = (node: ProsemirrorNode): DOMOutputSpec => {
    const attributes: { [key: string]: string } = {
      class: 'pm-columns',
      style: 'display: flex;',
    };

    if (this.htmlClassExtension) {
      const userClass = this.htmlClassExtension.getClassSerialization(node);
      if (userClass) {
        attributes.class += ' ' + userClass;
      }
    }

    return ['div', attributes, 0];
  };
}
export type ColumnsSchema = RtSchemaType<typeof ColumnsNode>;

export function createColumnsNodeView(): (
  node: ProsemirrorNode,
  view: EditorView,
  getPos: () => number,
) => NodeView {
  return (node, view, getPos) => {
    const dom = document.createElement('div');
    dom.className = 'pm-columns';
    dom.style.display = 'flex';
    dom.style.gap = '1em';

    const userClass = node.attrs['html-class'];
    if (userClass) {
      dom.classList.add(...userClass.split(/\s+/));
    }

    const contentDOM = document.createElement('div');
    contentDOM.className = 'pm-columns-inner';
    contentDOM.style.display = 'contents';
    dom.appendChild(contentDOM);

    dom.addEventListener('click', event => {
      const isInside = contentDOM.contains(<Node>event.target);
      if (!isInside && typeof getPos === 'function') {
        const pos = getPos();
        view.dispatch(
          view.state.tr.setSelection(NodeSelection.create(view.state.doc, pos)).scrollIntoView(),
        );
        view.focus();
        event.preventDefault();
      }
    });

    return {
      dom,
      contentDOM,
    };
  };
}
